import moment from 'moment';
import Vue from 'vue';
import { find, remove } from 'lodash';
import { Decimal } from 'decimal.js';
import CONST from '../const';
import QuotationRules from './business-logic/Quotation.rules';

/**
 *
 * @param {string} brand texto que indica la marca con la cual se mostrara el template
 */
const addTheme = (brand) => {
  const el = document.body;
  el.classList.remove('theme-vw');
  el.classList.remove('theme-audi');
  el.classList.remove('theme-seat');
  el.classList.remove('theme-default');

  switch (brand) {
    case 'VolkswagenLayout':
      el.classList.add('theme-vw');
      document.title = 'Volkswagen Live';
      break;
    case 'AudiLayout':
      el.classList.add('theme-audi');
      document.title = 'Audi Live';
      break;
    case 'SeatLayout':
      el.classList.add('theme-seat');
      document.title = 'Seat Live';
      break;
    default:
      el.classList.add('theme-default');
      break;
  }
};

const selectColor = (boxColor) => {
  let color = '';

  switch (boxColor) {
    case 'primary':
      color = 'text-primary';
      break;
    case 'secondary':
      color = 'text-secondary';
      break;
    case 'positive':
      color = 'text-positive';
      break;
    case 'info':
      color = 'text-info';
      break;
    case 'white':
      color = 'text-white';
      break;
    case '':
    case null:
    case undefined:
      color = 'text-white';
      break;
    default:
      color = boxColor;
  }

  return color;
};

const fileExtensionValidation = (path) => {
  const allowedExtensionsImage = /(.jpg|.jpeg|.png|.gif)$/i;
  const allowedExtensionsVideo = /(.mp4)$/i;

  const filename = path;

  if (allowedExtensionsImage.exec(filename)) {
    return 'image';
  }

  if (allowedExtensionsVideo.exec(filename)) {
    return 'video';
  }

  return undefined;
};

const formatDate = 'YYYY-MM-DD';

const addObject = (notification, data) => {
  const object = {
    title: '',
    date: '',
    notifications: [],
  };

  const now = moment(new Date());
  const date = moment(notification.date, formatDate);

  if (now.isSame(date)) {
    object.title = date.format('[Hoy] DD MMM YYYY');
  } else {
    object.title = date.format('DD MMM YYYY');
  }

  object.date = date.format(formatDate);
  object.notifications.push(notification);
  object.notifications.push(notification);
  data.push(object);

  return data;
};

const findData = (notification, data) => {
  for (let index = 0; index < data.length; index += 1) {
    const element = data[index];

    const dateElement = moment(element.date, formatDate);
    const dateNotification = moment(notification.date, formatDate);

    if (dateElement.isSame(dateNotification)) {
      element.notifications.push(notification);
      element.notifications.push(notification);
      return { element, index };
    }
  }

  return null;
};

const groupByDate = (notifications) => {
  let data = [];

  if (notifications.length === 0) return data;

  for (let index = 0; index < notifications.length; index += 1) {
    const notification = notifications[index];

    if (index === 0) {
      data = addObject(notification, data);
    }

    if (index > 0) {
      const object = findData(notification, data);

      if (object === null) {
        data = addObject(notification, data);
      } else {
        data[object.index] = object.element;
      }
    }
  }

  return data;
};

const base64ToArrayBuffer = (data) => {
  const binaryString = window.atob(data);
  const binaryLen = binaryString.length;
  const bytes = new Uint8Array(binaryLen);
  for (let i = 0; i < binaryLen; i += 1) {
    const ascii = binaryString.charCodeAt(i);
    bytes[i] = ascii;
  }
  return bytes;
};

const downloadFile = (base64Data, name, mime) => {
  const arrBuffer = base64ToArrayBuffer(base64Data);

  const newBlob = new Blob([arrBuffer], { type: mime });

  const data = window.URL.createObjectURL(newBlob);

  const link = document.createElement('a');
  document.body.appendChild(link);
  link.href = data;
  link.download = name;
  link.click();
  window.URL.revokeObjectURL(data);
  link.remove();
};

const remapActivitiesByType = (quotations) => {
  let subtotalPpso = 0;
  let subtotalPackagesDealer = 0;
  let subtotalSparepart = 0;
  let subtotalLabours = 0;
  let subtotalOtherCharges = 0;
  if (quotations) {
    const packagesPpso = quotations
      .filter((quotationItem) => QuotationRules.isPpsoItem(quotationItem));
    const packagesDealer = quotations
      .filter((quotationItem) => QuotationRules.isAgencyPackageItem(quotationItem));
    const spareparts = quotations
      .filter((quotationItem) => QuotationRules.isSparepart(quotationItem));
    const labours = quotations
      .filter((quotationItem) => QuotationRules.isLabour(quotationItem));
    const otherCharges = quotations
      .filter((quotationItem) => QuotationRules.isOtherCharge(quotationItem));

    const ppsoGroupPackage = QuotationRules.getPPsOByPackages(packagesPpso);

    subtotalPpso = ppsoGroupPackage.reduce(
      (accumulator, currentValue) => accumulator + +currentValue.pack_price,
      0,
    ).toFixed(2);

    subtotalPackagesDealer = packagesDealer.reduce(
      (accumulator, currentValue) => accumulator + +currentValue.pack_price,
      0,
    ).toFixed(2);

    subtotalSparepart = spareparts.reduce(
      (accumulator, currentValue) => accumulator
      + (currentValue.part_quantity * +currentValue.part_price),
      0,
    ).toFixed(2);

    subtotalLabours = labours.reduce(
      (accumulator, currentValue) => accumulator + +currentValue.part_price,
      0,
    ).toFixed(2);

    subtotalOtherCharges = otherCharges.reduce(
      (accumulator, currentValue) => accumulator + +currentValue.material_cost,
      0,
    ).toFixed(2);
  }

  const subtotalAmount = (
    +subtotalPpso
    + +subtotalPackagesDealer
    + +subtotalLabours
    + +subtotalOtherCharges
    + +subtotalSparepart
  ).toFixed(2);

  return {
    subtotalPpso,
    subtotalPackagesDealer,
    subtotalSparepart,
    subtotalLabours,
    subtotalOtherCharges,
    subtotalAmount,
  };
};

const validationItems = (
  orders,
  module,
  orderStatus,
  showAdditionalBudgets,
  showPreInvoice,
  showActivities,
  showInvoice,
  garantyOrder,
) => {
  let result = false;
  if (orderStatus === null) {
    result = false;
  }

  let options = [];
  switch (module) {
    case CONST.MODULE_ADDITIONAL_BUDGETS:
      options = [CONST.STATUS_ORDER_OPEN];

      if (garantyOrder) {
        result = true;
      } else {
        result = options.indexOf(orderStatus) >= 0 && showAdditionalBudgets;
      }

      break;
    case CONST.MODULE_PAYMENT:
      options = [CONST.STATUS_ORDER_OPEN];

      result = options.indexOf(orderStatus) >= 0 && showPreInvoice;
      break;
    case CONST.MODULE_RECEPTION:
      options = [CONST.STATUS_ORDER_OPEN, CONST.STATUS_ORDER_CLOSED];

      result = options.indexOf(orderStatus) >= 0;
      break;
    case CONST.MODULE_INVOICES:
      options = [CONST.STATUS_ORDER_CLOSED];
      if (showInvoice === 1) {
        result = true;
      } else {
        result = options.indexOf(orderStatus) >= 0 && showInvoice === 1;
      }
      // result = options.indexOf(orderStatus) >= 0;
      break;
    case CONST.MODULE_ACTIVITIES:
      options = [CONST.STATUS_ORDER_OPEN, CONST.STATUS_ORDER_CLOSED];

      result = options.indexOf(orderStatus) >= 0 && showActivities;
      break;
    default:
      break;
  }
  return result;
};

const validateAccess = (module) => {
  const orderStatus = Vue.prototype.$session.get('orderStatus') || null;
  const orders = Vue.prototype.$session.get('orders') || null;
  const showAdditionalBudgets = Vue.prototype.$session.get('showAdditionalBudgets') || false;
  const showPreInvoice = Vue.prototype.$session.get('showPreInvoice') || false;
  const showActivities = true; // TODO Pa despues...
  let result = false;
  let showInvoice = null;
  let garantyOrder = null;
  const statusOrder = [113, 112, 118, 121];
  if (orders) {
    orders.forEach((item) => {
      if (item.orderStatus === 113) {
        showInvoice = 1;
      }
      if (item.orderServiceType === 'Garantia' && statusOrder.indexOf(item.orderStatus) < 0) {
        garantyOrder = true;
      }
    });
  }
  result = validationItems(
    orders,
    module,
    orderStatus,
    showAdditionalBudgets,
    showPreInvoice,
    showActivities,
    showInvoice,
    garantyOrder,
  );
  return result;
};

const convertStringToObject = (restriction) => restriction
  .split(';')
  .map((item) => item.split(':').map((_) => _.trim()))
  .reduce((accumulator, currentValue) => {
    const newAcumulator = accumulator;
    const param = currentValue[0].toLowerCase();
    const index = 1;
    newAcumulator[param] = currentValue[index];
    return newAcumulator;
  }, {});

const checkRestrictions = (restrictions) => {
  // Convierte una cadena de string a un arreglo
  const newRestrictions = [];
  const array = restrictions.split('?').filter((i) => i);
  array.forEach((item) => {
    const obj = convertStringToObject(item);
    newRestrictions.push(obj);
  });
  remove(newRestrictions, (restriction) => restriction.description.includes('null,'));
  return newRestrictions;
};
const checkEstimatedData = (oldEstimatedDate, newEstimatedDate) => {
  let date = '';
  if (newEstimatedDate) {
    if (!oldEstimatedDate) date = newEstimatedDate;
    else {
      const oldDate = moment(oldEstimatedDate);
      const newDate = moment(newEstimatedDate);
      const diffDate = newDate.diff(oldDate, 'days');
      if (diffDate && diffDate > 0) date = newEstimatedDate;
      else date = oldEstimatedDate;
    }
  } else {
    date = oldEstimatedDate;
  }
  return date;
};

const validation1 = (clonedActivity) => (clonedActivity.pack_category === 'PP' || clonedActivity.pack_category === 'DP')
  && typeof activity === 'undefined' && clonedActivity.restrictions;

const validation2 = (clonedActivity, activity) => clonedActivity.pack_category === 'CP' && clonedActivity.part_brand === null && typeof activity === 'undefined';

const validation3 = (clonedActivity, activity) => clonedActivity.pack_category === 'CP' && clonedActivity.part_brand?.toLowerCase() === 'manodeobra' && typeof activity === 'undefined';

const validation4 = (clonedActivity, activity) => (clonedActivity.pack_category === 'PP' || clonedActivity.pack_category === 'DP') && activity;

const validation5 = (clonedActivity, activity) => clonedActivity.pack_category === 'CP' && clonedActivity.part_brand === null && activity;

const validation6 = (clonedActivity, activity) => clonedActivity.pack_category === 'CP' && clonedActivity.part_brand?.toLowerCase() === 'manodeobra' && activity;
/**
 *
 * @param {Object} activityAdd Se recibe actividad a ser agregada a listado
 * en caso de aplicar o cuyo costo se sumara a actividad existente en lista
 * @param {Array} activitiesList Lista de actividades en la cual se agregara
 * una nueva actividad en caso de aplicar o se sumara el costo a una actividad existente
 * @param {Array} packIds Lista que contiene los id´s de los paquetes que ya
 * han sido insertados dentro del listado de actividades
 * @returns Se retorna listado de actividades actualizado
 */
// ! TODO: Eliminar en siguiente versión mediante refactorización de reglas
// eslint-disable-next-line sonarjs/cognitive-complexity
const addActivity = (activityAdd, activitiesList, packIds) => {
  const clonedActivity = activityAdd;
  // Se valida si la actividad ya se encuentra dentro del listado
  const activity = find(
    activitiesList,
    (act) => act.order_activity_id === clonedActivity.order_activity_id,
  );
  /**
   * En caso de que la actividad este relacionada
   * a un paquete PPSo o concesionaria y no se encuentre en la lista
   * se inserta dentro de la lista de actividades y el pack_id se ingresa en el arreglo de id´s
   */
  if (validation1(clonedActivity)) {
    const restrictions = checkRestrictions(clonedActivity.restrictions);
    clonedActivity.restrictions = restrictions;
    if (clonedActivity.pack_category === 'PP' && !packIds.some((id) => clonedActivity.pack_id === id)) {
      activitiesList.push(clonedActivity);
      packIds.push(clonedActivity.pack_id);
    }
  }

  /**
   * Si la actividad esta relacionada a un Custom package,
   * no es mano de obra y no se encuentra en la lista
   * se multiplica el precio por la cantidad
   * de piezas y el resultado se inserta en pack_price y se ingresa a la lista
   */
  if (validation2(clonedActivity, activity)) {
    clonedActivity.pack_price = new Decimal(
      clonedActivity.part_price,
    ).mul(Decimal(clonedActivity.part_quantity)).toString();
    activitiesList.push(clonedActivity);
  }

  /**
   * Si la actividad esta relacionada
   * a un Custom package, es mano de obra y no se encuentra en la lista
   * se asigna el precio al pack_price y se inserta en la lista
   */
  if (validation3(clonedActivity, activity)) {
    clonedActivity.pack_price = clonedActivity.part_price;
    activitiesList.push(clonedActivity);
  }

  /**
   * En caso de que la actividad
   * este relacionada a un paquete PPSo o concesionario y se encuentre en la lista
   * se valida si el paquete ya fue insertado en caso de que no este en la lista de paquetes
   * se suma el pack_price de la actividad encontrada con el pack_price del paquete nuevo
   */
  if (validation4(clonedActivity, activity)) {
    const exist = find(packIds, (packId) => packId === clonedActivity.pack_id);
    if (exist == null) {
      activity.pack_price = new Decimal(activity.pack_price)
        .add(clonedActivity.pack_price).toString();
      packIds.push(clonedActivity.pack_id);
      activity.estimated_date = checkEstimatedData(
        activity.estimated_date,
        clonedActivity.estimated_date,
      );
      if (clonedActivity.pack_category === 'PP' && clonedActivity.restrictions && activity.restrictions !== null) {
        const restrictions = checkRestrictions(clonedActivity.restrictions);
        activity.restrictions = activity.restrictions.concat(restrictions);
      }
    }
  }

  /**
   * Si la actividad esta relacionada
   * a un Custom package, no es mano de obra y se encuentra en la lista
   * se multiplica el precio por la cantidad
   * de piezas y el resultado se suma al pack_price de la actividad encontrada
   */
  if (validation5(clonedActivity, activity)) {
    activity.pack_price = new Decimal(activity.pack_price).add(
      Decimal(clonedActivity.part_price).mul(Decimal(clonedActivity.part_quantity)),
    ).toString();
    activity.estimated_date = checkEstimatedData(
      activity.estimated_date,
      clonedActivity.estimated_date,
    );
  }

  /**
   * Si la actividad esta relacionada
   * a un Custom package, es mano de obra y se encuentra en la lista
   * se asigna se suma el precio al pack_price de la actividad encontrada
   */
  if (validation6(clonedActivity, activity)) {
    activity.pack_price = new Decimal(activity.pack_price)
      .add(clonedActivity.part_price).toString();
    activity.estimated_date = checkEstimatedData(
      activity.estimated_date,
      clonedActivity.estimated_date,
    );
  }

  if ((clonedActivity.pack_category === 'DP') && !packIds.some((id) => clonedActivity.pack_id === id)) {
    activitiesList.push(clonedActivity);
    packIds.push(clonedActivity.pack_id);
  }

  return activitiesList;
};

const removeDuplicatedPackages = (quotationItems) => {
  const filteredQuotationItems = [];

  quotationItems.forEach((item) => {
    if (!filteredQuotationItems.some((quotationItem) => quotationItem.pack_id === item.pack_id)) {
      filteredQuotationItems.push(item);
    }
  });

  return filteredQuotationItems;
};

export {
  checkEstimatedData,
  addActivity,
  addTheme,
  downloadFile,
  fileExtensionValidation,
  groupByDate,
  validateAccess,
  selectColor,
  removeDuplicatedPackages,
  remapActivitiesByType,
};
